/* eslint-disable no-unused-vars */

/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, Dispatch, ReactNode, useContext, useEffect, useMemo, useReducer } from 'react';
import Cookies from 'js-cookie';

// The Material Dashboard 2 PRO React TSUI Dashboard PRO Material main context
type MaterialUIContext = [StateTypes, Dispatch<ActionTypes>];

const MaterialUI = createContext<MaterialUIContext>(null);

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = 'MaterialUIContext';

// types
interface StateTypes {
    miniSidenav: boolean;
    transparentSidenav: boolean;
    whiteSidenav: boolean;
    sidenavColor:
        | 'primary'
        | 'secondary'
        | 'info'
        | 'success'
        | 'warning'
        | 'error'
        | 'light'
        | 'dark';
    transparentNavbar: boolean;
    fixedNavbar: boolean;
    openConfigurator: boolean;
    direction: 'ltr' | 'rtl';
    layout: 'dashboard' | 'page';
    darkMode: boolean;
    isLoading: boolean;
    userName: string;
}

type ActionTypes =
    | { type: 'MINI_SIDENAV'; value: boolean }
    | { type: 'TRANSPARENT_SIDENAV'; value: boolean }
    | { type: 'WHITE_SIDENAV'; value: boolean }
    | { type: 'SIDENAV_COLOR'; value: StateTypes['sidenavColor'] }
    | { type: 'TRANSPARENT_NAVBAR'; value: boolean }
    | { type: 'FIXED_NAVBAR'; value: boolean }
    | { type: 'OPEN_CONFIGURATOR'; value: boolean }
    | { type: 'DIRECTION'; value: StateTypes['direction'] }
    | { type: 'LAYOUT'; value: StateTypes['layout'] }
    | { type: 'DARKMODE'; value: boolean }
    | { type: 'SET_LOADING'; value: boolean }
    | { type: 'SET_USERNAME'; value: string };

// Material Dashboard 2 PRO React reducer
function reducer(state: StateTypes, action: ActionTypes) {
    switch (action.type) {
        case 'MINI_SIDENAV': {
            return { ...state, miniSidenav: action.value };
        }
        case 'TRANSPARENT_SIDENAV': {
            return { ...state, transparentSidenav: action.value };
        }
        case 'WHITE_SIDENAV': {
            return { ...state, whiteSidenav: action.value };
        }
        case 'SIDENAV_COLOR': {
            return { ...state, sidenavColor: action.value };
        }
        case 'TRANSPARENT_NAVBAR': {
            return { ...state, transparentNavbar: action.value };
        }
        case 'FIXED_NAVBAR': {
            return { ...state, fixedNavbar: action.value };
        }
        case 'OPEN_CONFIGURATOR': {
            return { ...state, openConfigurator: action.value };
        }
        case 'DIRECTION': {
            return { ...state, direction: action.value };
        }
        case 'LAYOUT': {
            return { ...state, layout: action.value };
        }
        case 'DARKMODE': {
            return { ...state, darkMode: action.value };
        }
        case 'SET_LOADING': {
            return { ...state, isLoading: action.value };
        }
        case 'SET_USERNAME': {
            return { ...state, userName: action.value };
        }
        default: {
            // Ação nova sem case aqui não compila.
            const unhandled: never = action;
            throw new Error(`Unhandled action: ${JSON.stringify(unhandled)}`);
        }
    }
}

// Material Dashboard 2 PRO React context provider
function MaterialUIControllerProvider({ children }: { children: ReactNode }): JSX.Element {
    // Helper function to validate sidebar color
    const isValidSidenavColor = (
        color: string | undefined
    ): color is
        | 'primary'
        | 'secondary'
        | 'info'
        | 'success'
        | 'warning'
        | 'error'
        | 'light'
        | 'dark' => {
        const validColors = [
            'primary',
            'secondary',
            'info',
            'success',
            'warning',
            'error',
            'light',
            'dark',
        ];
        return color !== undefined && validColors.includes(color);
    };

    // Initialize darkMode from cookies if available
    let initialDarkMode = true;
    let initialSidebarColor:
        | 'primary'
        | 'secondary'
        | 'info'
        | 'success'
        | 'warning'
        | 'error'
        | 'light'
        | 'dark' = 'success';

    try {
        const storedSideNavColor =
            typeof window !== 'undefined' ? Cookies.get('SidebarColor') : undefined;
        const storedDarkMode = typeof window !== 'undefined' ? Cookies.get('darkMode') : undefined;

        if (storedDarkMode !== undefined) {
            initialDarkMode = storedDarkMode === 'true';
        }

        if (isValidSidenavColor(storedSideNavColor)) {
            initialSidebarColor = storedSideNavColor;
        }
    } catch (error) {
        console.warn('Failed to load settings from cookies:', error);
    }

    const initialState: StateTypes = {
        miniSidenav: false,
        transparentSidenav: false,
        whiteSidenav: true,
        sidenavColor: initialSidebarColor,
        transparentNavbar: false,
        fixedNavbar: true,
        openConfigurator: false,
        direction: 'ltr',
        layout: 'dashboard',
        darkMode: initialDarkMode,
        isLoading: false,
        userName: '',
    };

    const [controller, dispatch] = useReducer(reducer, initialState);

    // Persist darkMode changes to cookies
    useEffect(() => {
        try {
            if (typeof window !== 'undefined') {
                Cookies.set('darkMode', controller.darkMode ? 'true' : 'false', {
                    expires: 365,
                });
            }
        } catch (error) {
            console.warn('Failed to save darkMode to cookies:', error);
        }
    }, [controller.darkMode]);

    useEffect(() => {
        try {
            if (typeof window !== 'undefined') {
                Cookies.set('SidebarColor', controller.sidenavColor, {
                    expires: 365,
                });
            }
        } catch (error) {
            console.warn('Failed to save SidebarColor to cookies:', error);
        }
    }, [controller.sidenavColor]);

    const value = useMemo<MaterialUIContext>(() => [controller, dispatch], [controller, dispatch]);

    return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 PRO React custom hook for using context
function useMaterialUIController() {
    const context = useContext(MaterialUI);

    if (!context) {
        throw new Error(
            'useMaterialUIController should be used inside the MaterialUIControllerProvider.'
        );
    }

    return context;
}

// Context module functions
const setMiniSidenav = (
    dispatch: (arg: { type: 'MINI_SIDENAV'; value: boolean }) => void,
    value: boolean
) => dispatch({ type: 'MINI_SIDENAV', value });
const setTransparentSidenav = (
    dispatch: (arg: { type: 'TRANSPARENT_SIDENAV'; value: boolean }) => void,
    value: boolean
) => dispatch({ type: 'TRANSPARENT_SIDENAV', value });
const setWhiteSidenav = (
    dispatch: (arg: { type: 'WHITE_SIDENAV'; value: boolean }) => void,
    value: boolean
) => dispatch({ type: 'WHITE_SIDENAV', value });
const setSidenavColor = (
    dispatch: (arg: {
        type: 'SIDENAV_COLOR';
        value:
            | 'primary'
            | 'secondary'
            | 'info'
            | 'success'
            | 'warning'
            | 'error'
            | 'light'
            | 'dark';
    }) => void,
    value: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'light' | 'dark'
) => dispatch({ type: 'SIDENAV_COLOR', value });
const setTransparentNavbar = (
    dispatch: (arg: { type: 'TRANSPARENT_NAVBAR'; value: boolean }) => void,
    value: boolean
) => dispatch({ type: 'TRANSPARENT_NAVBAR', value });
const setFixedNavbar = (
    dispatch: (arg: { type: 'FIXED_NAVBAR'; value: boolean }) => void,
    value: boolean
) => dispatch({ type: 'FIXED_NAVBAR', value });
const setOpenConfigurator = (
    dispatch: (arg: { type: 'OPEN_CONFIGURATOR'; value: boolean }) => void,
    value: boolean
) => dispatch({ type: 'OPEN_CONFIGURATOR', value });
const setDirection = (
    dispatch: (arg: { type: 'DIRECTION'; value: 'ltr' | 'rtl' }) => void,
    value: 'ltr' | 'rtl'
) => dispatch({ type: 'DIRECTION', value });
const setLayout = (
    dispatch: (arg: { type: 'LAYOUT'; value: 'dashboard' | 'page' }) => void,
    value: 'dashboard' | 'page'
) => dispatch({ type: 'LAYOUT', value });
const setDarkMode = (
    dispatch: (arg: { type: 'DARKMODE'; value: boolean }) => void,
    value: boolean
) => dispatch({ type: 'DARKMODE', value });
const setLoading = (
    dispatch: (arg: { type: 'SET_LOADING'; value: boolean }) => void,
    value: boolean
) => dispatch({ type: 'SET_LOADING', value });
const setUserName = (
    dispatch: (arg: { type: 'SET_USERNAME'; value: string }) => void,
    value: string
) => dispatch({ type: 'SET_USERNAME', value });

export {
    MaterialUIControllerProvider,
    useMaterialUIController,
    setMiniSidenav,
    setTransparentSidenav,
    setWhiteSidenav,
    setSidenavColor,
    setTransparentNavbar,
    setFixedNavbar,
    setOpenConfigurator,
    setDirection,
    setLayout,
    setDarkMode,
    setLoading,
    setUserName,
};
